import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Calendar, User, Phone, Send } from "lucide-react";
import haareImg from "@/assets/friseur-haarstyling-nuernberg.webp";
import facialsImg from "@/assets/gesichtsbehandlung-facial-nuernberg.webp";
import wimpernImg from "@/assets/wimpernverlaengerung-lashes-nuernberg.jpg";
import naegelImg from "@/assets/gelnaegel-nagelstudio-nuernberg.webp";
import haarentfernungImg from "@/assets/haarentfernung-glatte-haut-koerper.webp";
import beautyImg from "@/assets/beauty-studio-team-nuernberg.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import SEO from "@/components/SEO";

type Step = "service" | "time" | "date" | "name" | "phone" | "message" | "success";

const stepOrder: Step[] = ["service", "time", "date", "name", "phone", "message"];

interface FormData {
  service: string;
  preferredDate: string;
  preferredTime: string;
  name: string;
  phone: string;
  message: string;
}

const services = [
  { id: "haare", label: "Haare", img: haareImg, description: "Schnitt, Styling & Pflege" },
  { id: "facials", label: "Facials", img: facialsImg, description: "Gesichtsbehandlungen" },
  { id: "wimpern", label: "Wimpern", img: wimpernImg, description: "Lash- & Brow Lift" },
  { id: "naegel", label: "Nägel", img: naegelImg, description: "Maniküre & Pediküre" },
  { id: "haarentfernung", label: "Haarentfernung", img: haarentfernungImg, description: "Dauerhafte Haarentfernung" },
  { id: "sonstiges", label: "Sonstiges", img: beautyImg, description: "Andere Anfrage" },
];

const timeSlots = [
  { id: "vormittag", label: "Vormittag", time: "9–12 Uhr", icon: "🌅" },
  { id: "mittag", label: "Mittag", time: "12–15 Uhr", icon: "☀️" },
  { id: "nachmittag", label: "Nachmittag", time: "15–18 Uhr", icon: "🌤️" },
  { id: "abend", label: "Abend", time: "18–20 Uhr (Do & Fr)", icon: "🌙" },
  { id: "flexibel", label: "Flexibel", time: "Egal wann", icon: "✨" },
];

const AnfragePage = () => {
  const [step, setStep] = useState<Step>("service");
  const [formData, setFormData] = useState<FormData>({
    service: "",
    preferredDate: "",
    preferredTime: "",
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const currentIndex = stepOrder.indexOf(step);
  const progressValue = step === "success" ? 100 : ((currentIndex + 1) / stepOrder.length) * 100;

  const goNext = useCallback(() => {
    if (step === "success") return;
    const idx = stepOrder.indexOf(step);
    if (idx < stepOrder.length - 1) {
      setStep(stepOrder[idx + 1]);
    }
  }, [step]);

  const goPrev = useCallback(() => {
    const idx = stepOrder.indexOf(step);
    if (idx > 0) {
      setStep(stepOrder[idx - 1]);
    }
  }, [step]);

  const handleServiceSelect = (serviceId: string) => {
    setFormData((prev) => ({ ...prev, service: serviceId }));
    setTimeout(() => {
      setStep("time");
    }, 300);
  };

  const handleTimeSelect = (timeId: string) => {
    setFormData((prev) => ({ ...prev, preferredTime: timeId }));
    setTimeout(() => {
      setStep("date");
    }, 300);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const serviceLabel = services.find((s) => s.id === formData.service)?.label || formData.service;
      const timeLabel = timeSlots.find((t) => t.id === formData.preferredTime)?.label || formData.preferredTime;

      const { data, error } = await supabase.functions.invoke('send-anfrage', {
        body: {
          service: serviceLabel,
          preferredTime: timeLabel,
          preferredDate: formData.preferredDate,
          name: formData.name,
          phone: formData.phone,
          message: formData.message,
        },
      });

      if (error) throw error;

      setStep("success");
      toast({
        title: "Anfrage gesendet!",
        description: "Wir melden uns schnellstmöglich bei dir.",
      });
    } catch (error) {
      console.error('Error sending request:', error);
      toast({
        title: "Fehler",
        description: "Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case "service": return formData.service !== "";
      case "time": return formData.preferredTime !== "";
      case "date": return true;
      case "name": return formData.name.trim() !== "";
      case "phone": return formData.phone.trim() !== "";
      case "message": return true;
      default: return false;
    }
  };

  const stepLabel = () => {
    switch (step) {
      case "service": return "Schritt 1 von 6";
      case "time": return "Schritt 2 von 6";
      case "date": return "Schritt 3 von 6";
      case "name": return "Schritt 4 von 6";
      case "phone": return "Schritt 5 von 6";
      case "message": return "Schritt 6 von 6";
      default: return "";
    }
  };

  return (
    <div className="min-h-screen relative">
      <SEO
        title="Termin buchen & Kontakt – 22 Studios Nürnberg"
        description="Buchen Sie Ihren Beauty-Termin bei 22 Studios in der Deidesheimer Str. 4, Nürnberg – online oder telefonisch. Friseur, Facial, Nägel & mehr."
        canonical="https://22studios.de/nuernberg/anfrage"
      />
      <AnimatedBackground />
      <Navigation />

      <main className="relative z-10 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-lg mx-auto">

            {/* Header */}
            <div className="text-center mb-6 md:mb-8">
              <h1 className="text-heading md:text-display font-serif font-light text-foreground mb-2">
                Anfrage stellen
              </h1>
              <p className="text-muted-foreground text-sm">
                In wenigen Schritten zu deinem Wunschtermin
              </p>
            </div>

            {/* Progress Bar */}
            {step !== "success" && (
              <div className="mb-6 md:mb-10">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-muted-foreground">{stepLabel()}</span>
                  <span className="text-xs text-muted-foreground">{Math.round(progressValue)}%</span>
                </div>
                <Progress value={progressValue} className="h-2 bg-foreground/10" />
              </div>
            )}

            {/* Steps */}
            <div className="min-h-[320px] flex flex-col justify-center">

              {/* Step: Service */}
              {step === "service" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Welchen Service möchtest du?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Tippe auf deinen Wunsch
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {services.map((service) => {
                      const isSelected = formData.service === service.id;
                      return (
                        <button
                          key={service.id}
                          onClick={() => handleServiceSelect(service.id)}
                          className={`p-4 rounded-xl border transition-all text-center group overflow-hidden ${
                            isSelected
                              ? "bg-primary/20 border-primary shadow-lg scale-[1.02]"
                              : "bg-foreground/5 border-foreground/10 hover:bg-foreground/10 hover:border-foreground/20 active:scale-95"
                          }`}
                        >
                          <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2">
                            <img src={service.img} alt={service.label} className="w-full h-full object-cover" />
                          </div>
                          <h3 className="font-medium text-foreground text-sm">{service.label}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{service.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step: Time */}
              {step === "time" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Wann passt es dir am besten?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Wähle einen Zeitraum
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {timeSlots.map((slot) => {
                      const isSelected = formData.preferredTime === slot.id;
                      return (
                        <button
                          key={slot.id}
                          onClick={() => handleTimeSelect(slot.id)}
                          className={`p-4 rounded-xl border transition-all flex items-center gap-4 ${
                            isSelected
                              ? "bg-primary/20 border-primary shadow-lg"
                              : "bg-foreground/5 border-foreground/10 hover:bg-foreground/10 active:scale-[0.98]"
                          }`}
                        >
                          <span className="text-2xl">{slot.icon}</span>
                          <div className="text-left">
                            <span className="text-sm font-medium text-foreground block">{slot.label}</span>
                            <span className="text-xs text-muted-foreground">{slot.time}</span>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-primary ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step: Date */}
              {step === "date" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Hast du ein Wunschdatum?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Optional – du kannst auch überspringen
                  </p>
                  <div className="flex items-center gap-3 mb-4 justify-center">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <Input
                    type="date"
                    value={formData.preferredDate}
                    onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                    className="h-14 bg-foreground/5 border-foreground/10 rounded-xl text-center text-lg"
                  />
                </div>
              )}

              {/* Step: Name */}
              {step === "name" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Wie heißt du?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Damit wir dich ansprechen können
                  </p>
                  <div className="flex items-center gap-3 mb-4 justify-center">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <Input
                    placeholder="Dein Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && formData.name.trim() && goNext()}
                    className="h-14 bg-foreground/5 border-foreground/10 rounded-xl text-center text-lg"
                    autoFocus
                  />
                </div>
              )}



              {/* Step: Phone */}
              {step === "phone" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Deine Telefonnummer?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Damit wir dich kontaktieren können
                  </p>
                  <div className="flex items-center gap-3 mb-4 justify-center">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <Input
                    type="tel"
                    placeholder="+49 151 ..."
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && formData.phone.trim() && goNext()}
                    className="h-14 bg-foreground/5 border-foreground/10 rounded-xl text-center text-lg"
                    autoFocus
                  />
                </div>
              )}

              {/* Step: Message */}
              {step === "message" && (
                <div className="animate-fade-in">
                  <h2 className="text-xl font-medium text-foreground mb-2 text-center">
                    Noch etwas hinzufügen?
                  </h2>
                  <p className="text-muted-foreground text-sm text-center mb-6">
                    Optional – besondere Wünsche oder Fragen
                  </p>
                  <Textarea
                    placeholder="Besondere Wünsche, Allergien, Fragen..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="bg-foreground/5 border-foreground/10 rounded-xl resize-none text-base"
                    autoFocus
                  />

                  {/* Summary */}
                  <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                    <h4 className="font-medium text-foreground mb-3 flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Zusammenfassung
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 rounded-lg bg-background/50">
                        <span className="text-xs text-muted-foreground block">Service</span>
                        <span className="font-medium text-foreground">{services.find((s) => s.id === formData.service)?.label}</span>
                      </div>
                      <div className="p-2 rounded-lg bg-background/50">
                        <span className="text-xs text-muted-foreground block">Uhrzeit</span>
                        <span className="font-medium text-foreground">{timeSlots.find((t) => t.id === formData.preferredTime)?.label}</span>
                      </div>
                      {formData.preferredDate && (
                        <div className="p-2 rounded-lg bg-background/50">
                          <span className="text-xs text-muted-foreground block">Datum</span>
                          <span className="font-medium text-foreground">{formData.preferredDate}</span>
                        </div>
                      )}
                      <div className="p-2 rounded-lg bg-background/50">
                        <span className="text-xs text-muted-foreground block">Name</span>
                        <span className="font-medium text-foreground">{formData.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success */}
              {step === "success" && (
                <div className="animate-fade-in text-center py-8">
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-primary" />
                  </div>
                  <h2 className="text-2xl font-serif font-light text-foreground mb-3">
                    Vielen Dank!
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                    Wir haben deine Anfrage erhalten und melden uns schnellstmöglich bei dir.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 text-base font-medium rounded-full transition-all"
                  >
                    Zurück zur Startseite
                  </Link>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            {step !== "success" && (
              <div className="flex flex-col items-center gap-3 mt-12">
                <div className="flex justify-between w-full">
                  {currentIndex > 0 ? (
                    <Button variant="ghost" onClick={goPrev} className="gap-2 rounded-full">
                      <ArrowLeft size={16} />
                      Zurück
                    </Button>
                  ) : (
                    <div />
                  )}

                  {step === "service" || step === "time" ? (
                    <div />
                  ) : step === "message" ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8"
                    >
                      {isSubmitting ? "Wird gesendet..." : "Absenden"}
                      <Send size={16} />
                    </Button>
                  ) : (
                    <Button
                      onClick={goNext}
                      disabled={!canProceed()}
                      className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8"
                    >
                      Weiter
                      <ArrowRight size={16} />
                    </Button>
                  )}
                </div>

                {step === "date" && (
                  <button
                    onClick={goNext}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                  >
                    Überspringen
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnfragePage;
